import {ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';

import {debounceTime, map, switchMap, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {Destroyable} from '../../../destroyable.component';
import {Enums} from '../../utils/enums';
import {SdcLogsListComponent} from '../sdc-logs-list/sdc-logs-list.component';
import {PersonPayrollLogsListComponent} from '../sdc-person-logs-list/person-payroll-logs-list.component';
import {SDC3ConfigModes, SdcEntities, SdcEventTypes, SdcStatusDTO} from '../sdconnect.model';
import {SdconnectService} from '../sdconnect.service';

@Component({
  selector: 'gp-person-master-data-admin',
  templateUrl: './person-master-data-admin.component.html',
  styleUrls: ['./person-master-data-admin.component.scss']
})
export class PersonMasterDataAdminComponent extends Destroyable {

  protected readonly SdcEventTypes = SdcEventTypes;

  @ViewChild(SdcLogsListComponent)
  private sdcLogListComponent: SdcLogsListComponent;

  @ViewChild(PersonPayrollLogsListComponent)
  private personLogsListComponent: PersonPayrollLogsListComponent;

  public displayLatestPersonLogsOnly = true;

  public sdc3 = false;

  public contractId: string = null;
  public sdc2 = false;

  public activeLogTab: 'sdc' | 'person' = 'sdc';

  public modeDropdownOpen = false;
  public modeDropdownPosition = {top: 0, left: 0, minWidth: 0};

  public readonly SDC3ConfigModes = SDC3ConfigModes;
  public readonly sdc3ConfigModes: string[] = Enums.getNames(SDC3ConfigModes);
  public readonly sdc3ConfigEntities = [
    SdcEntities.Address, SdcEntities.BankAccount, SdcEntities.FamilyMember, SdcEntities.PersonHistory,
    SdcEntities.Contract, SdcEntities.Salary, SdcEntities.Organization, SdcEntities.Ledger, SdcEntities.WorkSchedule,
    SdcEntities.Facility, SdcEntities.CompanyCar];

  public sdc3ConfigFormGroup: FormGroup;

  constructor(
    private sdconnectService: SdconnectService,
    private toastrService: ToastrService,
    private formBuilder: FormBuilder,
    private cd: ChangeDetectorRef) {
    super();
    this.initSDC3Config();
  }

  public load(contractId: string, sdcStatus: SdcStatusDTO) {
    this.contractId = contractId;
    this.sdc2 = sdcStatus?.personMasterDataSyncOnSDC2Activated;
    this.sdc3 = sdcStatus?.personMasterDataSyncOnSDC3Activated;
    this.initSdc3ConfigFormGroupValues();
    this.cd.detectChanges();
  }

  public forceRefreshStatus() {
    this.sdconnectService.forceRefreshStatus(this.contractId).pipe(
      tap(() => {
        this.toastrService.success('Force refresh triggered, reload employee file in a few seconds.');
      })
    ).subscribe();
  }

  public forceSyncPersonToSDC3() {
    this.sdconnectService.forceSyncPersonToSDC3(this.contractId).pipe(
      tap(() => {
        this.toastrService.success(`Force sync person triggered, reload employee file in a few seconds.`);
      })
    ).subscribe();
  }

  public forceSyncPayrollToSDC3() {
    this.sdconnectService.forceSyncPayrollToSDC3(this.contractId).pipe(
      tap(() => {
        this.toastrService.success(`Force sync payroll triggered, reload employee file in a few seconds.`);
      })
    ).subscribe();
  }

  public sendTestMessageToSDC3() {
    this.sdconnectService.sendTestMessageToSDC3(this.contractId).pipe(
      tap(() => {
        this.toastrService.success(`Test message sent, check on SDConnect/LPE side in a few seconds.`);
      })
    ).subscribe();
  }

  public forceSyncData(cdmEntity: string) {
    this.sdconnectService.forceSyncData(this.contractId, cdmEntity).pipe(
      tap(() => {
        this.toastrService.success(`Force sync of ${cdmEntity} triggered, reload employee file in a few seconds.`);
      })
    ).subscribe();
  }

  public reloadSdcLogs() {
    this.sdcLogListComponent.reload();
    this.sdconnectService.sdcLogsRefreshed$.next();
  }

  public reloadPersonLogs() {
    this.personLogsListComponent.reload();
  }

  public reloadActiveLogs() {
    if (this.activeLogTab === 'sdc') {
      this.reloadSdcLogs();
    } else {
      this.reloadPersonLogs();
    }
  }

  public toggleDisplayLatestPersonLogsOnly(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.displayLatestPersonLogsOnly = inputElement.checked;
  }

  public toggleModeDropdown(event: Event) {
    event.stopPropagation();
    if (!this.modeDropdownOpen) {
      const trigger = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.modeDropdownPosition = {
        top: trigger.bottom + 4,
        left: trigger.left,
        minWidth: trigger.width
      };
    }
    this.modeDropdownOpen = !this.modeDropdownOpen;
  }

  public closeModeDropdown() {
    this.modeDropdownOpen = false;
  }

  public selectMode(mode: string) {
    this.sdc3ConfigFormGroup.controls.mode.setValue(mode);
    this.closeModeDropdown();
  }

  private initSDC3Config() {
    this.initSDC3ConfigFormGroup();
    this.bindSDC3ConfigValueChanges();
  }

  private bindSDC3ConfigValueChanges() {
    this.sdc3ConfigFormGroup.controls.mode.valueChanges.pipe(
      tap(mode => {
        if (mode === SDC3ConfigModes.DEFAULT) {
          this.sdc3ConfigFormGroup.controls.partials.patchValue(
            this.sdc3ConfigEntities.reduce((acc, entity) => ({...acc, [entity]: true}), {}),
            {emitEvent: false});
        }
      })
    ).subscribe();
    this.sdc3ConfigFormGroup.valueChanges.pipe(
      debounceTime(650),
      switchMap(config => this.sdconnectService.createOrUpdateSdc3Config(this.contractId, config)),
      tap(() => this.toastrService.success('SDC3 config updated successfully'))
    ).subscribe();
  }

  private initSDC3ConfigFormGroup() {
    this.sdc3ConfigFormGroup = this.formBuilder.group({
      mode: [SDC3ConfigModes.DEFAULT],
      partials: this.formBuilder.group(this.sdc3ConfigEntities.reduce((acc, entity) => ({
        ...acc,
        [entity]: [true]
      }), {})),
    });
  }

  private initSdc3ConfigFormGroupValues() {
    this.sdconnectService.getSdc3Config(this.contractId)
      .pipe(map(sdc3Config =>
        this.sdc3ConfigFormGroup.patchValue({
          mode: sdc3Config.mode,
          partials: sdc3Config.partials
        }, {emitEvent: false})
      ))
      .subscribe();
  }
}
